import React, {
  useCallback
} from 'react'
    
import { checkDotNet, getHost } from './utilities'
import { APIClient } from './useAPIClient'
import { useArtifacts } from './useArtifacts'
import { ProjectRecipeFilter, RecipeInterface } from '@pollination-solutions/pollination-sdk'
import { usePollinationPanel } from './usePollinationPanel'
import dayjs from 'dayjs'
import { LocalArtifactData } from '../SelectLocalArtifactNative/SelectLocalArtifactNative.types'
import { Buffer } from 'buffer'

declare global {
  interface Window {
    sketchup?: any
      chrome?: any
  }
}

interface JobArgs {
  name: string,
  recipe: RecipeInterface,
  description?: string,
  recipeFilter?: ProjectRecipeFilter
  authUserName?: string
}

export interface ProjectJobInfo {
  accountName: string
  studyId: string
  projectName?: string
  projectDescription?: string
  jobName?: string
  jobDescription?: string
  platform?: string
  subfolder?: string
}

export interface LocalConfig {
  localCPUCount: number,
  localRunFolder: LocalArtifactData,
  isLocalJob: boolean,
  cloudProjectName: string,
  cloudProjectOwner: string
  useHandlers: boolean
}

/**
 * From base64 to File
 * @param data base64 string
 * @param filename file name
 * @returns File
 */
const dataURLtoFile = (data: string, filename: string) => {
  const bufferObject = Buffer.from(data, 'base64')
  return new File([bufferObject], filename, { type: 'text/plain' })
}

export const useCreateStudy = (accountName: string, projectName: string, client: APIClient) => {
  const host: string = getHost()

  const { uploadArtifact, downloadArtifact } = useArtifacts(accountName, projectName, client)

  const { saveFileBase64 } = usePollinationPanel()

  // uploads all file inputs for cloud studies
  const processEntryCloud = useCallback(([name, value], input) => new Promise((resolve, reject) => {
    const { type } = input

    if (type !== 'DAGFileInput' && type !== 'DAGFolderInput') {
      resolve({
        name,
        type: 'JobArgument',
        value
      })
    }
      
    // true for files selected via SelectCloudArtifacts
    if (typeof value.key === 'string') {
      resolve({
        name,
        type: 'JobPathArgument',
        source: {
          type: 'ProjectFolder',
          path: value.key
        }
      })
    }
    
    // true for files selected via SelectLocalArtifactNative
    if (typeof value.pollination_local_path === 'string') {
      reject(
        'Running cloud simulations with local artifacts is not supported.'
      )
    }

    // true for files selected via SelectLocalArtifactNative
    if (typeof value.local_root === 'string') {
      reject(
        'Running cloud simulations with local folder is not supported.'
      )
    }
    
    // from Base64 to File. Use it for controlled values local machine to cloud 
    if (typeof value.base64_string === 'string' && typeof value.file_name === 'string') {
      value = dataURLtoFile(value.base64_string, value.file_name)
    }

    // true for file uploaders
    if (value instanceof File) {
      if (!uploadArtifact) {
        reject('useCreateStudy: uploadArtifact is undefined but the user needs to upload input files.')
      }

      resolve(uploadArtifact(undefined , value)
        .then((data) => ({
          name,
          type: 'JobPathArgument',
          source: {
            type: 'ProjectFolder',
            path: value.name
          }
        }))
      )
    }

    // FileInput as recursive
    if (Array.isArray(value)) {
      resolve(Promise.all(value.map(_ => uploadArtifact(undefined , _)))
        .then((data) => ({
          name,
          type: 'JobPathArgument',
          source: {
            type: 'ProjectFolder',
            path: value[0].webkitRelativePath.split('/')[0]
          }})
        )
      )
    }
  }),[uploadArtifact])

  // downloads, saves all file inputs for handling by plugins for local runs
  // regardless of whether it's a local run or cloud run, platform will handle input files
  const processEntryLocal = useCallback(([name, value], input, 
    cloudProjectName?: string, useHandlers?: boolean ) => 
    new Promise((resolve, reject) => {

      const { type } = input
      const alias = useHandlers && input?.alias?.find(a => a.platform.includes(host))

      if (alias) {
        // Linked file input
        if (alias.type === 'DAGLinkedInputAlias'
          && type === 'DAGFileInput' 
          // placeholder
          && typeof value === 'string'
          && value.startsWith('linked')) {
            resolve({
              name,
              type: 'JobPathArgument',
              source: {
                type: 'ProjectFolder',
                path: value
              }
            })
        }
        
        // Linked data
        if (alias.type === 'DAGLinkedInputAlias'
          && type !== 'DAGFileInput' 
          // placeholder
          && typeof value === 'string'
          && value.startsWith('linked')) {
            resolve({
              name,
              type: 'JobArgument',
              value
            })
        }

        // // Folder input as array
        // if (alias.type !== 'DAGArrayInputAlias'
        // && type === 'DAGFolderInput' 
        // && Array.isArray(value)) {
        //   resolve({
        //     name,
        //     type: 'JobArgument',
        //     value
        //   })
        // }
      }

      if (type !== 'DAGFileInput' && type !== 'DAGFolderInput') {
        resolve({
          name,
          type: 'JobArgument',
          value
        })
      }

      // Input from SelectLocalArtifactNative as file
      if (typeof value.pollination_local_path === 'string') {
        resolve({
          name,
          type: 'JobPathArgument',
          source: {
            type: 'ProjectFolder',
            path: value.pollination_local_path
          }
        })
      }

      // Input from SelectLocalArtifactNative as recursive
      if (typeof value.local_root === 'string') {
        resolve({
          name,
          type: 'JobPathArgument',
          source: {
            type: 'ProjectFolder',
            path: value.local_root
          }
        })
      }

      // from Base64 to File. Use it for controlled values local machine to cloud 
      if (typeof value.base64_string === 'string' && typeof value.file_name === 'string') {
        value = dataURLtoFile(value.base64_string, value.file_name)
      }

      // first download artifact and turn it into a blob
      // then save it to the users machine and provide  
      // the name to the jobInfo object
      if (typeof value.key === 'string' && value.file_type === 'file') {
        if (value.name == null) {
          reject(`'name' attribute of ${value.key} is null. Job will be invalid.`)
        }

        downloadArtifact(value.key)
          .then(url => {
            fetch(url)
              .then(response => response.blob().then(blob => {
                saveFileBase64('key', blob, cloudProjectName ?? `${dayjs().format('YYYY-DD-MMTHH-mm-ss')}`, value.name)
                  .then(message => {
                    if (!message || !message.data) {
                      reject(`Cloud Artifact ${value.name} could not be saved to the local machine. Try a cloud study.`)
                    }
                  
                    resolve({
                      name,
                      type: 'JobPathArgument',
                      source: {
                        type: 'ProjectFolder',
                        path: message.data
                      }
                    })
                  })
                  .catch((e) => {
                    reject(e)
                  })
              }))
          })
      }

      if (typeof value.key === 'string' && value.file_type === 'folder') {
        reject('[WIP]: Work in progress...')
      }

      // files from CAD apps
      if (value instanceof File) {
        saveFileBase64('key', value, cloudProjectName ?? `${dayjs().format('YYYY-DD-MM')}`, value.name)
          .then(message => {
            if (!message || !message.data) {
              reject(`File ${value.name} could not be saved to the local machine. Try a cloud study.`)
            }
            
            resolve({
              name,
              type: 'JobPathArgument',
              source: {
                type: 'ProjectFolder',
                path: message.data
              }
            })
          })
          .catch((e) => {
            reject(e)
          })
      }
    }),[downloadArtifact, saveFileBase64])
  
  const createStudy = useCallback((
    {
      name, 
      recipe, 
      description 
    }: JobArgs, 
    data: object, 
    onSuccess?: (projectJobInfo: ProjectJobInfo) => void
  ) => {
    if (!client || !name || !recipe || !data) {
      return new Promise<void>((resolve, reject) => resolve())
    }
    
    return Promise.all(Object.entries(data).map((d) => { 
      const input = recipe.inputs.find(i => i.name === d[0])
      return processEntryCloud(d, input)
    }))
      .then(jobArguments => {
        const job = {
          type: 'Job',
          api_version: 'v1beta1',
          name,
          description,
          source: recipe.source,
          arguments: [jobArguments]
        }
        return client.jobs.createJob({
          owner: accountName,
          name:  projectName,
          // @ts-ignore
          job
        })
          .then((res) => {
            onSuccess && onSuccess({
              accountName: accountName, 
              projectName: projectName, 
              jobName: name, 
              jobDescription: description, 
              studyId: res.data.id
            })
          })
          .catch((err) => {
            const { data } = err.response
            throw new Error(data.detail)
          })
      })
  },[accountName, client, processEntryCloud, projectName])

  const createLocalStudy = useCallback(async (
    { 
      name, 
      description,
      recipe,
      recipeFilter,
      authUserName
    }: JobArgs, 
    data: object, 
    { 
      localCPUCount, 
      localRunFolder, 
      isLocalJob,
      cloudProjectName,
      cloudProjectOwner,
      useHandlers
    }: LocalConfig,
    onSuccess?: (projectJobInfo: ProjectJobInfo) => void,
    userRecipe?: RecipeInterface,
    key: string = performance.now().toString()
  ) => {    
    if (!checkDotNet || !client || !uploadArtifact) return
    if (!name || !recipe || !data) return

    // If user edits recipe at runtime use it
    if (userRecipe) recipe = userRecipe

    const job = await Promise.all(Object.entries(data).map((d) => {
      const input = recipe.inputs.find(i => i.name === d[0])
      return processEntryLocal(d, input, cloudProjectName, useHandlers)
    }))
      .then(jobArguments =>{ 
        return ({
          type: 'Job',
          api_version: 'v1beta1',
          name,
          description,
          source: recipe.source,
          arguments: [jobArguments]
        })
      }).catch((err) => {
        console.error(err)
      })
    
    // Try getting account
    let author = undefined
    try {
      const { data } = await client.accounts.getAccount({ name: authUserName })
      author = data
    } catch (err) {
      console.log(err)
    }

    // JobInfo mapping
    const jobInfo ={
      RecipeOwner: recipeFilter.owner ?? 'ladybug-tools', // If Recipe form it is not available
      Recipe: recipe,
      Job: job,
      IsLocalJob: isLocalJob,
      ProjectSlug: `${cloudProjectOwner}/${cloudProjectName}`,
      LocalCPUNumber: localCPUCount,
      LocalRunFolder: localRunFolder.pollination_local_path,
      Platform: host,
      JobAuthor: author,
      UseHandlers: useHandlers
    }

    console.log(jobInfo)
    
    // TODO: Add handlers
    const response = window.parent.chrome.webview.hostObjects.study.RunSimulation(
      JSON.stringify(jobInfo))
      .then(
        (value) => {
          onSuccess && onSuccess({
            accountName: cloudProjectOwner, 
            projectName: cloudProjectName, 
            jobName: name, 
            jobDescription: description, 
            studyId: value
          })
        },
        (reason) => {
          console.error(reason) 
        },
      )

    return response

  }, [client, processEntryLocal, uploadArtifact])
  
  return {
    host,
    createStudy,
    createLocalStudy
  }
}